import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Button, alpha,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  Public as PublicIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Flag as FlagIcon,
  Badge as BadgeIcon,
  OpenInNew as OpenInNewIcon,
  Circle as CircleIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import SystemInformationSection from '../../common/SystemInformationSection';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canAccessPartnershipManagement, canDeleteCompany } from '../../../utils/partnershipPermissions';
import { resolveUserId, userDetailsPath } from '../../../utils/userDetailsLink';
import StorageImage from '../../common/StorageImage';
import { injectDetailsPageStyles } from '../../common/DetailsPagePrimitives';

// ─── Design tokens (identical to RequirementDetails) ─────────────────────────
const NEON        = '#38BDF8';
const NEON2       = '#818CF8';
const NEON3       = '#34D399';
const AMBER       = '#FBBF24';
const CORAL       = '#F87171';
const SURFACE_MID = '#0a1628';
const SURFACE_UP  = '#0f1f38';
const SURFACE_TOP = '#152640';
const TEXT_BODY   = '#E2E8F0';
const TEXT_MUTED  = '#64748B';
const TEXT_SUB    = '#94A3B8';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const trimProtocol = (url) => (url || '').replace(/^https?:\/\//, '');

const webHref = (u) => {
  if (!u) return null;
  const s = String(u).trim();
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

const telHref = (phone) => {
  if (!phone) return null;
  const s = String(phone).trim();
  return s ? `tel:${s}` : null;
};

const prettifyStatus = (s) => {
  if (!s) return null;
  if (s === 'follow_up') return 'Follow Up';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const formatAuditUser = (record, field) =>
  field === 'created'
    ? record?.creator?.name || record?.created_by_name || '-'
    : record?.last_updater?.name || record?.updated_by_name || '-';

const STATUS_CONFIG = {
  active:    { color: NEON3,  label: 'Active'    },
  inactive:  { color: CORAL,  label: 'Inactive'  },
  pending:   { color: AMBER,  label: 'Pending'   },
  follow_up: { color: NEON2,  label: 'Follow Up' },
  prospect:  { color: NEON,   label: 'Prospect'  },
};

// ─── Sub-components (mirrors RequirementDetails patterns exactly) ─────────────

/** Animated section wrapper */
const Section = ({ title, icon: Icon, accent = NEON, delay = 0, children }) => (
  <Box
    sx={{
      borderRadius: '16px',
      border: `1px solid ${alpha(accent, 0.14)}`,
      background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
      overflow: 'hidden',
      animation: `fadeUp 0.4s ease ${delay}s both`,
      '@keyframes fadeUp': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to:   { opacity: 1, transform: 'translateY(0)' },
      },
    }}
  >
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

/** Compact info row */
const InfoRow = ({ icon: Icon, label, value, href, onClick, color = NEON, iconColor }) => {
  const hasValue = Boolean(value);
  const isInteractive = Boolean((href || onClick) && hasValue);
  const content = (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      py: 0.9, px: 1.2, borderRadius: '9px',
      transition: 'background 0.15s',
      cursor: isInteractive ? 'pointer' : 'default',
      '&:hover': isInteractive ? { background: alpha(color, 0.06) } : {},
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
        background: alpha(iconColor || color, 0.1),
        border: `1px solid ${alpha(iconColor || color, 0.18)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 14, color: iconColor || alpha(color, 0.85) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.65rem', color: TEXT_MUTED,
          fontFamily: "'DM Sans', sans-serif",
          textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600,
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '0.88rem', color: isInteractive ? color : TEXT_BODY,
          fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {hasValue ? value : '-'}
        </Typography>
      </Box>
      {hasValue && (href || onClick) && <OpenInNewIcon sx={{ fontSize: 12, color: alpha(color, 0.45), flexShrink: 0 }} />}
    </Box>
  );
  if (hasValue && href) {
    const isExternalWeb = /^https?:\/\//i.test(href);
    return (
      <a
        href={href}
        {...(isExternalWeb ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        style={{ textDecoration: 'none', display: 'block' }}
      >
        {content}
      </a>
    );
  }
  if (hasValue && onClick) {
    return (
      <Box role="link" tabIndex={0} onClick={onClick} onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
      }} sx={{ textDecoration: 'none', display: 'block' }}>
        {content}
      </Box>
    );
  }
  return content;
};

/** Large highlighted metric card — same as RequirementDetails */
const MetricCard = ({ label, value, sub, icon: Icon, color = NEON, onClick, href }) => {
  const card = (
  <Box
    role={onClick ? 'link' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
    } : undefined}
    sx={{
    position: 'relative',
    borderRadius: '14px', px: 2.5, py: 2,
    height: '100%',           
    boxSizing: 'border-box',
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'box-shadow 0.25s, border-color 0.25s',
    '&:hover': {
      boxShadow: `0 0 36px ${alpha(color, 0.16)}, inset 0 1px 0 ${alpha(color, 0.18)}`,
      borderColor: alpha(color, 0.35),
    },
  }}>
    <Box sx={{
      position: 'absolute', top: -16, right: -16,
      width: 70, height: 70, borderRadius: '50%',
      background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none',
    }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{
        fontSize: '0.67rem', fontFamily: "'DM Sans', sans-serif",
        color: TEXT_MUTED, letterSpacing: '0.09em',
        textTransform: 'uppercase', fontWeight: 600,
      }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{
      fontSize: '1.25rem', fontWeight: 700,
      fontFamily: "'Rajdhani', sans-serif",
      color, letterSpacing: 0.3, lineHeight: 1.15,
    }}>
      {value || '-'}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>
        {sub}
      </Typography>
    )}
  </Box>
  );
  if (value && href) {
    return <a href={href} style={{ textDecoration: 'none', display: 'block' }}>{card}</a>;
  }
  return card;
};

/** Full-screen lightbox overlay (portal — escapes Section transform stacking) */
const ImageLightbox = ({ src, alt, onClose }) => {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    setZoom(1);
  }, [src]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <Box
      onClick={onClose}
      sx={{
        position: 'fixed', inset: 0, zIndex: 1400,
        background: 'rgba(3, 8, 18, 0.95)',
        backdropFilter: 'blur(12px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        p: { xs: 1, sm: 2 },
        animation: 'lbIn 0.2s ease both',
        '@keyframes lbIn': { from: { opacity: 0 }, to: { opacity: 1 } },
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'relative',
          width: 'min(96vw, fit-content)',
          maxHeight: '96vh',
          borderRadius: '16px', overflow: 'hidden',
          border: `1px solid ${alpha(NEON, 0.2)}`,
          background: alpha('#060b16', 0.98),
          boxShadow: `0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px ${alpha(NEON, 0.1)}`,
          animation: 'lbScale 0.25s cubic-bezier(0.34,1.56,0.64,1) both',
          '@keyframes lbScale': { from: { transform: 'scale(0.88)' }, to: { transform: 'scale(1)' } },
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.7,
          zIndex: 2,
        }}>
          <Button
            onClick={() => setZoom((z) => Math.min(z + 0.15, 3))}
            sx={{
              minWidth: 0, width: 38, height: 38, borderRadius: '8px',
              border: `1px solid ${alpha(NEON, 0.32)}`,
              background: alpha(SURFACE_MID, 0.82), color: NEON,
              '&:hover': { background: alpha(NEON, 0.12), borderColor: alpha(NEON, 0.6) },
            }}
          >
            <ZoomInIcon sx={{ fontSize: 18 }} />
          </Button>
          <Button
            onClick={() => setZoom((z) => Math.max(z - 0.15, 0.5))}
            sx={{
              minWidth: 0, width: 38, height: 38, borderRadius: '8px',
              border: `1px solid ${alpha(NEON, 0.32)}`,
              background: alpha(SURFACE_MID, 0.82), color: NEON,
              '&:hover': { background: alpha(NEON, 0.12), borderColor: alpha(NEON, 0.6) },
            }}
          >
            <ZoomOutIcon sx={{ fontSize: 18 }} />
          </Button>
          <Button
            onClick={onClose}
            sx={{
              minWidth: 0, width: 38, height: 38, borderRadius: '8px',
              border: `1px solid ${alpha(CORAL, 0.4)}`,
              background: alpha(SURFACE_MID, 0.82), color: CORAL,
              '&:hover': { background: alpha(CORAL, 0.12), borderColor: alpha(CORAL, 0.75) },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>

        <Box sx={{
          overflow: 'auto',
          maxHeight: '96vh',
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <StorageImage
            storagePath={src}
            alt={alt}
            sx={{
              display: 'block',
              width: 'auto',
              height: 'auto',
              maxWidth: '94vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              transform: `scale(${zoom})`,
              transformOrigin: 'center center',
              transition: 'transform 0.25s ease',
            }}
          />
        </Box>
        <Typography sx={{
          position: 'absolute',
          left: 14,
          bottom: 10,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.62rem',
          color: alpha(TEXT_SUB, 0.8),
          letterSpacing: '0.05em',
        }}>
          ZOOM: {Math.round(zoom * 100)}%
        </Typography>
      </Box>
    </Box>,
    document.body,
  );
};

/** Clickable image card — opens lightbox on click */
const ImageCard = ({ title, src, alt }) => {
  const [open, setOpen] = useState(false);
  const resolvedSrc = src || null;

  return (
    <>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{
          fontSize: '0.65rem', fontFamily: "'DM Sans', sans-serif",
          fontWeight: 700, color: TEXT_MUTED,
          textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1,
        }}>
          {title}
        </Typography>
        <Box
          onClick={() => resolvedSrc && setOpen(true)}
          sx={{
            position: 'relative',
            borderRadius: '12px', overflow: 'hidden',
            border: `1px solid ${alpha(NEON, 0.15)}`,
            background: alpha(SURFACE_UP, 0.6),
            minHeight: 140,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: resolvedSrc ? 'zoom-in' : 'default',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            '&:hover': resolvedSrc ? {
              borderColor: alpha(NEON, 0.45),
              boxShadow: `0 0 20px ${alpha(NEON, 0.12)}`,
              '& .zoom-hint': { opacity: 1 },
            } : {},
          }}
        >
          {resolvedSrc ? (
            <>
              <StorageImage
                storagePath={resolvedSrc}
                alt={alt}
                sx={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
              />
              {/* Hover zoom hint */}
              <Box
                className="zoom-hint"
                sx={{
                  position: 'absolute', inset: 0,
                  background: alpha(SURFACE_MID, 0.55),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0, transition: 'opacity 0.2s',
                }}
              >
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: 1.5, py: 0.7, borderRadius: '20px',
                  background: alpha(NEON, 0.15),
                  border: `1px solid ${alpha(NEON, 0.4)}`,
                }}>
                  <ZoomInIcon sx={{ fontSize: 15, color: NEON }} />
                  <Typography sx={{ fontSize: '0.72rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, color: NEON }}>
                    View full size
                  </Typography>
                </Box>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3, px: 2 }}>
              <ImageIcon sx={{ fontSize: 28, color: alpha(NEON, 0.25), mb: 0.75 }} />
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, fontSize: '0.78rem' }}>
                No image uploaded
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {open && (
        <ImageLightbox src={resolvedSrc} alt={alt} onClose={() => setOpen(false)} />
      )}
    </>
  );
};

/** Static logo avatar in hero (fullscreen view only in Media Assets section) */
const LogoAvatar = ({ src, alt }) => (
  <Box
    sx={{
      width: 54, height: 54, borderRadius: '14px', flexShrink: 0,
      border: `2px solid ${alpha(NEON2, 0.35)}`,
      overflow: 'hidden',
      position: 'relative',
    }}
  >
    <StorageImage storagePath={src} alt={alt} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const CompanyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const hasPartnershipAccess = canAccessPartnershipManagement(currentUser);

  const [company,          setCompany]          = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState(null);
  const [successMessage,   setSuccessMessage]   = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => { injectDetailsPageStyles(); }, []);

  useEffect(() => {
    if (!hasPartnershipAccess) { navigate('/dashboard'); return; }
    const fetchCompany = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/companies/${id}`);
        setCompany(response.data);
        setError(null);
      } catch {
        setError('Failed to load company details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, [id, hasPartnershipAccess, navigate]);

  const handleDelete = async () => {
    try {
      const name = company?.name || 'Company';
      const response = await api.post(`/companies/${id}/archive`);
      if (response.data?.company) {
        setCompany(response.data.company);
      }
      setDeleteDialogOpen(false);
      setSuccessMessage(`Company "${name}" archived successfully!`);
      setTimeout(() => navigate('/companies', { state: { refreshList: Date.now() } }), 2000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to archive company. Please try again.');
      setDeleteDialogOpen(false);
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message="Loading company details" size={56} variant="primary" />
    </Box>
  );

  if (!company) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <FeedbackMessage
        message={error || 'Company not found.'}
        severity="error"
        sx={{ fontFamily: "'DM Sans', sans-serif", background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }}
      />
    </Container>
  );

  // Normalise field aliases
  const companyEmail    = company.gmail || company.email;
  const companyPhone    = company.contact_number || company.phone;
  const companyWebsite  = company.website_link || company.website;
  const companyLocation = company.location || company.address;
  const igPath          = company.ig_path || company.instagram_path;

  const statusCfg = STATUS_CONFIG[company.status] ?? { color: TEXT_SUB, label: prettifyStatus(company.status) || '-' };

  const socialLinks = [
    { icon: FacebookIcon,  label: 'Facebook',  value: company.facebook_path, color: '#1877F2' },
    { icon: InstagramIcon, label: 'Instagram', value: igPath,                color: '#E4405F' },
    { icon: YouTubeIcon,   label: 'YouTube',   value: company.youtube_path,  color: '#FF0000' },
    { icon: LinkIcon,      label: 'Other',     value: company.other_path,    color: NEON2     },
  ];

  const repName  = company.representative?.name  || company.representative_name;
  const assignedNames = (company.assignees || []).map((member) => member.name).filter(Boolean).join(', ');
  const repEmail = company.representative?.email || company.representative_email;
  const repId = resolveUserId(company.representative ?? company.representative_user_id);
  const repPhonePrimary = company.representative?.phone
    || company.representative?.phone_number
    || company.representative?.contact_number
    || company.representative?.mobile_phone
    || company.representative_phone_number
    || company.representative_phone;

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 8 }}>

      <Header
        title="Company Details"
        subtitle="View company information"
        icon={BusinessIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
        actionButton={
          hasPartnershipAccess && (
            <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
              <Button
                startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                onClick={() => navigate(`/companies/edit/${id}`)}
                sx={{
                  borderRadius: '10px', px: 2.2, py: 0.65,
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem',
                  background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.55)}`,
                  color: NEON, transition: 'all 0.2s',
                  '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 14px ${alpha(NEON, 0.2)}` },
                }}
              >
                Edit
              </Button>
              {canDeleteCompany(currentUser, company) && (
              <Button
                startIcon={<ArchiveIcon sx={{ fontSize: 15 }} />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  borderRadius: '10px', px: 2.2, py: 0.65,
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem',
                  background: 'transparent', border: `1.5px solid ${alpha(CORAL, 0.55)}`,
                  color: CORAL, transition: 'all 0.2s',
                  '&:hover': { background: alpha(CORAL, 0.08), borderColor: CORAL, boxShadow: `0 0 14px ${alpha(CORAL, 0.2)}` },
                }}
              >
                Archive
              </Button>
              )}
            </Box>
          )
        }
      />

      {error && (
        <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
          sx={{ mb: 2.5, fontFamily: "'DM Sans', sans-serif", background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }}
        />
      )}
      {successMessage && (
        <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage(null)}
          sx={{ mb: 2.5, fontFamily: "'DM Sans', sans-serif", background: alpha(NEON3, 0.08), border: `1px solid ${alpha(NEON3, 0.3)}`, color: NEON3 }}
        />
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* ══════════════════════════════════════════
            HERO — cover image background + metric strip
            ══════════════════════════════════════════ */}
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          overflow: 'hidden',
          position: 'relative',
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to:   { opacity: 1, transform: 'translateY(0)' },
          },
        }}>
          {/* Cover image background */}
          {company.company_cover_picture && (
            <StorageImage
              storagePath={company.company_cover_picture}
              alt="Cover"
              sx={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: 'center',
                opacity: 0.18,
                pointerEvents: 'none',
              }}
            />
          )}
          {/* Gradient overlay — always present, deeper when no cover */}
          <Box sx={{
            position: 'absolute', inset: 0,
            background: company.company_cover_picture
              ? `linear-gradient(170deg, ${alpha(SURFACE_UP, 0.55)} 0%, ${alpha(SURFACE_MID, 0.92)} 60%, ${alpha(SURFACE_MID, 0.99)} 100%)`
              : `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
            pointerEvents: 'none',
          }} />

          {/* Content sits above the bg layers */}
          <Box sx={{ position: 'relative', p: { xs: 2, md: 2.5 } }}>
            {/* Identity row */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Company logo — clickable if exists */}
                {company.company_logo ? (
                  <LogoAvatar src={company.company_logo} alt={company.name || 'Company logo'} />
                ) : (
                  <Box sx={{
                    width: 54, height: 54, borderRadius: '14px', flexShrink: 0,
                    background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`,
                    border: `2px solid ${alpha(NEON2, 0.3)}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <BusinessIcon sx={{ fontSize: 24, color: NEON2 }} />
                  </Box>
                )}

                <Box>
                  <Typography sx={{
                    fontSize: '1.45rem', fontWeight: 700,
                    fontFamily: "'Rajdhani', sans-serif",
                    color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15,
                  }}>
                    {company.name || '-'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                    {[companyLocation, companyWebsite ? trimProtocol(companyWebsite) : null]
                      .filter(Boolean).join(' · ') || '-'}
                  </Typography>
                </Box>
              </Box>

              {/* Status pill */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.7,
                px: 1.4, py: 0.5, borderRadius: '20px',
                background: alpha(statusCfg.color, 0.1),
                border: `1px solid ${alpha(statusCfg.color, 0.3)}`,
                alignSelf: 'flex-start',
                backdropFilter: 'blur(6px)',
              }}>
                <CircleIcon sx={{ fontSize: 7, color: statusCfg.color }} />
                <Typography sx={{
                  fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700, color: statusCfg.color, letterSpacing: '0.05em',
                }}>
                  {statusCfg.label}
                </Typography>
              </Box>
            </Box>

            {/* Metric strip */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              <MetricCard label="Pipeline"      value={statusCfg.label}  icon={FlagIcon}     color={statusCfg.color} />
              <MetricCard label="Phone" value={companyPhone} icon={PhoneIcon} color={NEON} href={telHref(companyPhone)} />
              <MetricCard label="Assigned Members" value={assignedNames || repName} icon={PersonIcon} color={NEON3} sub={!assignedNames ? (repEmail || undefined) : undefined} onClick={!assignedNames && repId ? () => navigate(userDetailsPath(repId)) : undefined} />
              <MetricCard label="Location"       value={companyLocation}  icon={LocationIcon} color={NEON2} />
            </Box>

          </Box>
        </Box>

        {/* ══════════════════════════════════════════
            Media Assets
            ══════════════════════════════════════════ */}
        <Section title="Media Assets" icon={ImageIcon} delay={0.08}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <ImageCard title="Company Logo" src={company.company_logo} alt="Logo" />
            <ImageCard title="Cover Image"  src={company.company_cover_picture} alt="Cover" />
          </Box>
        </Section>

        {/* ══════════════════════════════════════════
            Contact Information  |  Person in Charge
            ══════════════════════════════════════════ */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>

          <Section title="Contact Information" icon={PhoneIcon} delay={0.1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
              <InfoRow icon={PhoneIcon} label="Phone Number" value={companyPhone} href={telHref(companyPhone)} />
              <InfoRow icon={EmailIcon}    label="Email Address" value={companyEmail}
                href={companyEmail ? `mailto:${companyEmail}` : null}
              />
              <InfoRow icon={LocationIcon} label="Location"      value={companyLocation} />
              <InfoRow icon={WebsiteIcon}  label="Website"       value={trimProtocol(companyWebsite)}
                href={webHref(companyWebsite)}
              />
            </Box>
          </Section>

          <Section title="Person in Charge (External)" icon={PersonIcon} accent={NEON3} delay={0.12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
              <InfoRow icon={PersonIcon} label="Name"  value={company.person_in_charge_name}  color={NEON3} />
              <InfoRow icon={EmailIcon}  label="Email" value={company.person_in_charge_email} color={NEON3}
                href={company.person_in_charge_email ? `mailto:${company.person_in_charge_email}` : null}
              />
              <InfoRow icon={PhoneIcon} label="Phone" value={company.person_in_charge_phone} color={NEON3}
                href={telHref(company.person_in_charge_phone)}
              />
            </Box>
          </Section>
        </Box>

        {/* ══════════════════════════════════════════
            Description — only when present (mirrors Purpose block in RequirementDetails)
            ══════════════════════════════════════════ */}
        <Section title="Description" icon={DescriptionIcon} delay={0.13}>
          <Box sx={{
            px: 2, py: 1.8, borderRadius: '10px',
            borderLeft: `3px solid ${alpha(NEON, 0.4)}`,
            background: alpha(SURFACE_TOP, 0.4),
          }}>
            <Typography sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.92rem', color: TEXT_BODY, lineHeight: 1.75,
            }}>
              {company.description || '-'}
            </Typography>
          </Box>
        </Section>

        {/* ══════════════════════════════════════════
            Social Media — only rendered when links exist
            ══════════════════════════════════════════ */}
        <Section title="Social Media" icon={PublicIcon} accent={NEON2} delay={0.16}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
            {socialLinks.map(s => (
              <InfoRow
                key={s.label}
                icon={s.icon}
                label={s.label}
                value={s.value ? trimProtocol(s.value) : null}
                href={webHref(s.value)}
                color={s.color}
                iconColor={s.color}
              />
            ))}
          </Box>
        </Section>

        {/* ══════════════════════════════════════════
            Representative (Internal)
            ══════════════════════════════════════════ */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>

          <Section title="Representative (Internal)" icon={BadgeIcon} delay={0.19}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
              <InfoRow
                icon={PersonIcon}
                label="Name"
                value={repName}
                onClick={repId ? () => navigate(userDetailsPath(repId)) : undefined}
              />
              <InfoRow icon={EmailIcon}  label="Email" value={repEmail}
                href={repEmail ? `mailto:${repEmail}` : null}
              />
              <InfoRow icon={PhoneIcon} label="Phone Number" value={repPhonePrimary} href={telHref(repPhonePrimary)} />
            </Box>
          </Section>
        </Box>

        {/* ══════════════════════════════════════════
            System Information
            ══════════════════════════════════════════ */}
        <SystemInformationSection
          createdBy={formatAuditUser(company, 'created')}
          createdAt={company.created_at ? format(new Date(company.created_at), 'dd MMM yyyy, h:mm a') : null}
          updatedBy={formatAuditUser(company, 'updated')}
          updatedAt={company.updated_at ? format(new Date(company.updated_at), 'dd MMM yyyy, h:mm a') : null}
          delay={0.23}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />

      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        itemType="company"
        itemName={company?.name}
        title="Archive Company"
        description="This will mark the company as inactive and keep its history, files, and linked partner records."
        confirmLabel="Archive Company"
        actionVerb="archive"
      />
    </Container>
  );
};

export default CompanyDetails;
